import { CapabilityDetailLayout } from '@/components/capability-detail/CapabilityDetailLayout';
import { PromptMarkdownTab } from '@/components/prompts/PromptMarkdownTab';
import { useFileRevisionEditor, type FileResolverArgs } from '@/hooks/use-file-revision-editor';

function resolvePromptFilePath({ files, typeConfig }: FileResolverArgs): string {
    const defs = typeConfig?.definitions;
    if (Array.isArray(defs)) {
        for (const d of defs) {
            if (typeof d !== 'string') continue;
            const exact = files.find((f) => f.path === d);
            if (exact) return exact.path;
            const bySuffix = files.find((f) => f.path.endsWith(d) || f.path.endsWith(`/${d}`));
            if (bySuffix) return bySuffix.path;
        }
        const first = defs.find((d): d is string => typeof d === 'string');
        if (first) return first;
    }
    if (files.length > 0) {
        const byPromptMd = files.find((f) => f.path.endsWith('prompt.md'));
        if (byPromptMd) return byPromptMd.path;
        const md = files.find((f) => /\.md$/i.test(f.path));
        if (md) return md.path;
        return files[0].path;
    }
    return 'prompt.md';
}

export interface PromptDetailPageProps {
    appKey?: string;
    name?: string;
    /** When provided, CapabilityDetailLayout pushes center + versionSelector to this header layer. */
    headerLayerId?: string;
}

export default function PromptDetailPage({
    appKey: appKeyProp,
    name: nameProp,
    headerLayerId,
}: PromptDetailPageProps = {}) {
    const editor = useFileRevisionEditor({
        resolveFilePath: resolvePromptFilePath,
        primaryContentType: 'text/markdown',
        logPrefix: 'prompt-detail-page',
        saveSuccessMessage: 'Prompt saved',
        saveErrorMessage: 'Failed to save prompt',
        appKey: appKeyProp,
        capabilityName: nameProp,
    });

    return (
        <CapabilityDetailLayout
            typeLabel="Prompt"
            appKey={appKeyProp}
            capabilityName={nameProp}
            supplementalDeferredSave={editor.supplementalDeferredSave}
            selectedRevisionId={editor.selectedRevisionId}
            onRevisionChange={editor.setSelectedRevisionId}
            headerLayerId={headerLayerId}
        >
            <PromptMarkdownTab
                value={editor.displayedValue}
                onChange={editor.setDraft}
                isLoading={editor.displayedLoading}
                hasWorkingRevision={editor.hasWorkingRevision && !editor.isViewingPublished}
                isArchived={editor.isArchived || editor.isViewingPublished}
            />
        </CapabilityDetailLayout>
    );
}
